import { useTranslation } from "react-i18next";

import type { PlanningConfig } from "@/api/planningConfig";
import { CommittedInput } from "@/components/committed-input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

interface MethodParamsProps {
  config: PlanningConfig;
  disabled: boolean;
  onChange: (patch: Partial<PlanningConfig>) => void;
}

export function MethodParams({
  config,
  disabled,
  onChange,
}: MethodParamsProps) {
  if (config.method === "ets")
    return (
      <EtsParams config={config} disabled={disabled} onChange={onChange} />
    );
  if (config.method === "croston")
    return (
      <CrostonParams config={config} disabled={disabled} onChange={onChange} />
    );
  if (config.method === "static")
    return (
      <StaticParams config={config} disabled={disabled} onChange={onChange} />
    );
  return null;
}

function EtsParams({ config, disabled, onChange }: MethodParamsProps) {
  const { t } = useTranslation();
  const season = String(config.etsSeasonLength);
  const damped = config.etsDamped;
  return (
    <div className="border-muted flex flex-col gap-2 border-l-2 pl-3">
      <div className="flex items-center gap-2">
        <Label className="text-muted-foreground w-20 text-xs">
          {t("items.detail.method.params.ets.season")}
        </Label>
        <Select
          value={season}
          onValueChange={(v) => onChange({ etsSeasonLength: Number(v) })}
          disabled={disabled}
        >
          <SelectTrigger className="h-7 w-32 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">
              {t("items.detail.method.params.ets.seasonNone")}
            </SelectItem>
            <SelectItem value="4">
              {t("items.detail.method.params.ets.seasonQuarterly")}
            </SelectItem>
            <SelectItem value="12">
              {t("items.detail.method.params.ets.seasonYearly")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2">
        <Label className="text-muted-foreground w-20 text-xs">
          {t("items.detail.method.params.ets.damped")}
        </Label>
        <Switch
          checked={damped}
          disabled={disabled}
          onCheckedChange={(v) => onChange({ etsDamped: v })}
        />
      </div>
    </div>
  );
}

function CrostonParams({ config, disabled, onChange }: MethodParamsProps) {
  const { t } = useTranslation();
  const variant = config.crostonVariant;
  return (
    <div className="border-muted flex flex-col gap-2 border-l-2 pl-3">
      <div className="flex items-center gap-2">
        <Label className="text-muted-foreground w-20 text-xs">
          {t("items.detail.method.params.croston.variant")}
        </Label>
        <Select
          value={variant}
          onValueChange={(v) =>
            onChange({ crostonVariant: v as "classic" | "sba" | "tsb" })
          }
          disabled={disabled}
        >
          <SelectTrigger className="h-7 w-32 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="classic">
              {t("items.detail.method.params.croston.variantClassic")}
            </SelectItem>
            <SelectItem value="sba">
              {t("items.detail.method.params.croston.variantSba")}
            </SelectItem>
            <SelectItem value="tsb">
              {t("items.detail.method.params.croston.variantTsb")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function StaticParams({ config, disabled, onChange }: MethodParamsProps) {
  const { t } = useTranslation();
  return (
    <div className="border-muted flex flex-col gap-2 border-l-2 pl-3">
      <div className="flex items-center gap-2">
        <Label className="text-muted-foreground w-20 text-xs">
          {t("items.detail.method.params.static.assumedMonthlyDemand")}
        </Label>
        <CommittedInput
          type="number"
          min={0}
          step="0.1"
          className="h-7 w-32 text-xs"
          value={String(config.assumedMonthlyDemand ?? 0)}
          onCommit={(raw) =>
            onChange({ assumedMonthlyDemand: raw === "" ? 0 : Number(raw) })
          }
          disabled={disabled}
        />
      </div>
      <p className="text-muted-foreground text-xs">
        {t("items.detail.method.params.static.help")}
      </p>
    </div>
  );
}
